import type { StudentRegistrationRecordsDataItem } from "@oneapp/data";

type ScoreBannerProfile = {
  vertical_id?: number | null;
  is_default_profile?: boolean;
  uam_id?: number | null;
};

type ProfilesDataLike =
  | {
      unified_account_details?: {
        unified_account_id?: string | null;
        profiles?: ScoreBannerProfile[] | null;
      } | null;
    }
  | null
  | undefined;

type CombinedOrderSubscriptionLike = {
  packageName?: string | null;
  product?: { name?: string | null } | null;
  userOrderPackageSubscriptionsDto?: {
    effectiveDate?: string | null;
    expiryDate?: string | null;
  } | null;
};

type CombinedOrderLike = {
  mySubcriptionDtoList?: CombinedOrderSubscriptionLike[] | null;
};

export const SCORE_BANNER_BLOCKED_PACKAGE_NAMES = [
  "books package",
  "ultimate package",
  "ultimate pro package",
  "pro package",
] as const;

export function getSelectedProfile(
  profiles: ScoreBannerProfile[] | null | undefined,
): ScoreBannerProfile | undefined {
  if (!profiles?.length) return undefined;
  if (profiles.length === 1) return profiles[0];
  return profiles.find((profile) => profile.is_default_profile) ?? profiles[0];
}

export function getScoreAuthContext(profilesData: ProfilesDataLike): {
  verticalId: number | undefined;
  userId: string | null;
  uamId: number | null;
} {
  const profiles = profilesData?.unified_account_details?.profiles ?? [];
  const selectedProfile = getSelectedProfile(profiles);
  return {
    verticalId: selectedProfile?.vertical_id ?? undefined,
    userId: profilesData?.unified_account_details?.unified_account_id ?? null,
    uamId: selectedProfile?.uam_id ?? null,
  };
}

export function groupBy<T, K extends PropertyKey>(
  items: T[],
  keySelector: (item: T) => K,
): Record<K, T[]> {
  return items.reduce(
    (acc, item) => {
      const key = keySelector(item);
      (acc[key] ??= []).push(item);
      return acc;
    },
    {} as Record<K, T[]>,
  );
}

export type ScoreRegistrationTimeState =
  | "upcoming"
  | "live"
  | "missed_or_completed";

export type ScoreRegistrationSlotState =
  | "upcoming"
  | "live_attempt"
  | "live_resume"
  | "missed"
  | "completed";

/** Matches `/tests/score`: live from 10 minutes before start through end. */
export const SCORE_LIVE_THRESHOLD_MS = 10 * 60 * 1000;

type ScoreRegistrationSlotLike = Pick<
  StudentRegistrationRecordsDataItem,
  "datetime" | "enddatetime" | "isExamSubmittedByUser" | "isExamInProgress"
>;

export function classifyScoreRegistrationSlot(
  slot: ScoreRegistrationSlotLike,
  getNow?: () => number,
): ScoreRegistrationSlotState | null {
  const start = new Date(slot.datetime ?? "").getTime();
  const end = new Date(slot.enddatetime ?? "").getTime();
  if (!Number.isFinite(start) || !Number.isFinite(end)) return null;

  const now = getNow?.() ?? Date.now();
  const live = now >= start - SCORE_LIVE_THRESHOLD_MS && now <= end;
  const afterEnd = now > end;

  if (slot.isExamSubmittedByUser) return "completed";
  if (slot.isExamInProgress) return afterEnd ? "missed" : "live_resume";
  if (afterEnd) return "missed";
  if (live) return "live_attempt";
  return "upcoming";
}

function parseScoreEndDateMs(endDate?: string | null): number | null {
  const trimmed = endDate?.trim();
  if (!trimmed) return null;
  const endMs = new Date(trimmed).getTime();
  return Number.isFinite(endMs) ? endMs : null;
}

/** Earliest valid end timestamp when both personal and slot ends exist. */
export function clampScoreExamEndMs(
  personalEndMs: number | null,
  slotEndMs: number | null,
): number | null {
  if (personalEndMs != null && slotEndMs != null) {
    return Math.min(personalEndMs, slotEndMs);
  }
  return personalEndMs ?? slotEndMs;
}

export function computeScoreExamEndMs(params: {
  startedAt?: string | null;
  durationMinutes?: number | null;
  slotEndDate?: string | null;
}): number | null {
  const startedAt = params.startedAt?.trim();
  const durationMinutes = Number(params.durationMinutes);
  let personalEndMs: number | null = null;

  if (startedAt && Number.isFinite(durationMinutes) && durationMinutes > 0) {
    const startMs = new Date(startedAt).getTime();
    if (Number.isFinite(startMs)) {
      personalEndMs = startMs + durationMinutes * 60_000;
    }
  }

  const slotEndMs = parseScoreEndDateMs(params.slotEndDate);
  return clampScoreExamEndMs(personalEndMs, slotEndMs);
}

/** Slot window end — matches ScoreActivityCard schedule (`enddatetime ?? endtimeslot`). */
export function getScoreActivitySlotEndMs(
  slot: Pick<
    StudentRegistrationRecordsDataItem,
    "enddatetime" | "endtimeslot" | "date"
  >,
): number | null {
  return (
    parseScoreEndDateMs(slot.enddatetime) ??
    parseScoreEndDateMs(slot.endtimeslot) ??
    parseScoreEndDateMs(slot.date)
  );
}

/** Remaining exam time for a live in-progress attempt, clamped to slot end. */
export function getScoreActivityExamEndMs(
  slot: Pick<
    StudentRegistrationRecordsDataItem,
    "started_at" | "duration" | "enddatetime" | "endtimeslot" | "date"
  >,
): number | null {
  return computeScoreExamEndMs({
    startedAt: slot.started_at,
    durationMinutes: Number(slot.duration),
    slotEndDate:
      slot.enddatetime?.trim() ||
      slot.endtimeslot?.trim() ||
      slot.date?.trim() ||
      null,
  });
}

export type ScoreRegistrationBuckets = {
  liveResume: StudentRegistrationRecordsDataItem[];
  liveAttempt: StudentRegistrationRecordsDataItem[];
  upcoming: StudentRegistrationRecordsDataItem[];
  missed: StudentRegistrationRecordsDataItem[];
  completed: StudentRegistrationRecordsDataItem[];
};

function getSlotStartMs(slot: StudentRegistrationRecordsDataItem): number {
  const startMs = new Date(slot.datetime ?? slot.date ?? "").getTime();
  return Number.isFinite(startMs) ? startMs : Number.MAX_SAFE_INTEGER;
}

export function pickSoonestSlot(
  slots: StudentRegistrationRecordsDataItem[],
): StudentRegistrationRecordsDataItem | undefined {
  if (!slots.length) return undefined;

  return [...slots].sort((a, b) => getSlotStartMs(a) - getSlotStartMs(b))[0];
}

export function pickLatestSlot(
  slots: StudentRegistrationRecordsDataItem[],
): StudentRegistrationRecordsDataItem | undefined {
  if (!slots.length) return undefined;

  return [...slots].sort((a, b) => getSlotStartMs(b) - getSlotStartMs(a))[0];
}

/** Score-program carousel: live → upcoming → completed → missed. */
export function pickScoreProgramDisplaySlot(
  buckets: ScoreRegistrationBuckets,
): StudentRegistrationRecordsDataItem | undefined {
  const live = [...buckets.liveResume, ...buckets.liveAttempt];
  if (live.length > 0) return pickSoonestSlot(live);
  if (buckets.upcoming.length > 0) return pickSoonestSlot(buckets.upcoming);
  if (buckets.completed.length > 0) return pickLatestSlot(buckets.completed);
  if (buckets.missed.length > 0) return pickLatestSlot(buckets.missed);
  return undefined;
}

/** Non-score banner: soonest live/resume/attempt/upcoming only. */
export function pickNonScoreProgramDisplaySlot(
  buckets: ScoreRegistrationBuckets,
): StudentRegistrationRecordsDataItem | undefined {
  const active = [
    ...buckets.liveResume,
    ...buckets.liveAttempt,
    ...buckets.upcoming,
  ];
  return pickSoonestSlot(active);
}

export function classifyScoreRegistrationBuckets(
  onlineSlots: StudentRegistrationRecordsDataItem[] | null | undefined,
  getNow: () => number,
): ScoreRegistrationBuckets {
  const buckets: ScoreRegistrationBuckets = {
    liveResume: [],
    liveAttempt: [],
    upcoming: [],
    missed: [],
    completed: [],
  };

  for (const item of onlineSlots ?? []) {
    const state = classifyScoreRegistrationSlot(item, getNow);
    if (!state) continue;
    if (state === "live_resume") buckets.liveResume.push(item);
    else if (state === "live_attempt") buckets.liveAttempt.push(item);
    else if (state === "upcoming") buckets.upcoming.push(item);
    else if (state === "missed") buckets.missed.push(item);
    else buckets.completed.push(item);
  }

  return buckets;
}

export function getScoreSlotStatus(
  startDate?: string | null,
  endDate?: string | null,
  getNow?: () => number,
): ScoreRegistrationTimeState | null {
  if (!startDate || !endDate) return null;
  try {
    const start = new Date(startDate).getTime();
    const end = new Date(endDate).getTime();
    if (!Number.isFinite(start) || !Number.isFinite(end)) return null;

    const now = getNow?.() ?? Date.now();

    if (now > end) return "missed_or_completed";
    if (now >= start) return "live";
    if (now < start) return "upcoming";
    return null;
  } catch (error) {
    return null;
  }
}

export function hasScoreStudentRegistration(
  studentRecords: StudentRegistrationRecordsDataItem[] | null | undefined,
  getNow: () => number,
): {
  isScoreOfflineRegistered: boolean;
  onlineScoreSlots: StudentRegistrationRecordsDataItem[];
  isScoreOnlineRegistered: boolean;
  registrationBuckets: ScoreRegistrationBuckets;
  scoreProgramDisplaySlot: StudentRegistrationRecordsDataItem | undefined;
  nonScoreProgramDisplaySlot: StudentRegistrationRecordsDataItem | undefined;
  hasAnyOnlineRegistration: boolean;
} {
  if (!Array.isArray(studentRecords))
    return {
      isScoreOfflineRegistered: false,
      onlineScoreSlots: [],
      isScoreOnlineRegistered: false,
      registrationBuckets: {
        liveResume: [],
        liveAttempt: [],
        upcoming: [],
        missed: [],
        completed: [],
      },
      scoreProgramDisplaySlot: undefined,
      nonScoreProgramDisplaySlot: undefined,
      hasAnyOnlineRegistration: false,
    };

  const { online = [], offline = [] } = groupBy(
    studentRecords,
    (item) => item.mode?.toLowerCase() ?? "",
  );

  const registrationBuckets = classifyScoreRegistrationBuckets(online, getNow);
  const liveOrUpcoming = [
    ...registrationBuckets.liveResume,
    ...registrationBuckets.liveAttempt,
    ...registrationBuckets.upcoming,
  ];
  const scoreProgramDisplaySlot =
    pickScoreProgramDisplaySlot(registrationBuckets);
  const nonScoreProgramDisplaySlot =
    pickNonScoreProgramDisplaySlot(registrationBuckets);
  const onlineScoreSlots = scoreProgramDisplaySlot
    ? [scoreProgramDisplaySlot]
    : [];

  return {
    isScoreOfflineRegistered: offline.length > 0,
    // Gating: active live/upcoming registration only (not past-only).
    isScoreOnlineRegistered: liveOrUpcoming.length > 0,
    onlineScoreSlots,
    registrationBuckets,
    scoreProgramDisplaySlot,
    nonScoreProgramDisplaySlot,
    hasAnyOnlineRegistration: (online ?? []).length > 0,
  };
}

function normalizePackageName(
  subscription: CombinedOrderSubscriptionLike,
): string {
  const name =
    subscription.product?.name?.trim() ||
    subscription.packageName?.trim() ||
    "";
  return name.toLowerCase();
}

function isBlockedPackageName(packageName: string): boolean {
  return SCORE_BANNER_BLOCKED_PACKAGE_NAMES.includes(
    packageName as (typeof SCORE_BANNER_BLOCKED_PACKAGE_NAMES)[number],
  );
}

/** Active = started (effectiveDate <= today) and not expired (expiryDate >= today). */
export function isActiveCombinedOrderSubscription(
  subscription: CombinedOrderSubscriptionLike,
  getNow: () => number,
): boolean {
  const { effectiveDate, expiryDate } =
    subscription.userOrderPackageSubscriptionsDto ?? {};

  if (!expiryDate) return false;

  const today = new Date(getNow());
  today.setHours(0, 0, 0, 0);

  const expiry = new Date(expiryDate);
  if (Number.isNaN(expiry.getTime())) return false;
  expiry.setHours(0, 0, 0, 0);

  if (expiry < today) return false;

  if (effectiveDate) {
    const effective = new Date(effectiveDate);
    if (!Number.isNaN(effective.getTime())) {
      effective.setHours(0, 0, 0, 0);
      if (effective > today) return false;
    }
  }

  return true;
}

export function hasScoreBannerBlockingPackage(
  combinedOrders: CombinedOrderLike[] | null | undefined,
  getNow: () => number,
): boolean {
  if (!Array.isArray(combinedOrders)) return true;

  for (const order of combinedOrders) {
    for (const subscription of order.mySubcriptionDtoList ?? []) {
      // first i am checking any package is active or not
      if (!isActiveCombinedOrderSubscription(subscription, getNow)) continue;

      // Check is package name is Ultimate, Ultimate Pro, Books Package
      const packageName = normalizePackageName(subscription);
      if (isBlockedPackageName(packageName)) {
        return false;
      }
    }
  }

  return true;
}
export const SCORE_PROGRAM_TITLE = "score";
